// Dialog zum Anlegen/Bearbeiten eines Eintrags der Pflege "Finanzielle
// Unterstützung" (DCP-2228 follow-up, 14.09.2026)
//
// Vereinfachte Übertragung von PaymentNoteTemplateFormDialog.tsx: kein
// etag/Rich-Text, dafür ein Teilnehmerkreis-Select (Vorbild:
// EventFormDialog.tsx's audience-Select) und eine Veranstaltungstypen-
// Checkbox-Liste (Vorbild: EventFormDialog.tsx's Verkaufskanäle-Block).
// Serverfehler (409-Konflikt, 404) erscheinen im Dialog, nicht als Toast —
// gleiches Muster wie beim Vorbild.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEventTypes } from '@/services/event-service';
import { EventAudience, type FinancialSupport, type SaveFinancialSupportRequest } from '@/types';

interface Props {
  open: boolean;
  /** null = anlegen; sonst bearbeiten. */
  entry: FinancialSupport | null;
  /** Serverfehler (409-Konflikt, 404): erscheint im Dialog, nicht als Toast. */
  saveError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSave: (req: SaveFinancialSupportRequest) => void;
  isSaving?: boolean;
}

export interface FinancialSupportFormValues {
  name: string;
  audience: EventAudience;
  /** Als Text im Formular (Komma-Eingabe möglich), erst beim Speichern in eine Zahl geparst. */
  flatRateAmount: string;
  vaTypes: string[];
  proofRequired: boolean;
}

const emptyForm = (): FinancialSupportFormValues => ({
  name: '',
  audience: EventAudience.Public,
  flatRateAmount: '',
  vaTypes: [],
  proofRequired: false,
});

/** Die Fehler, die diese Prüfung melden kann — als i18n-Schlüssel. */
export type FinancialSupportFormError =
  | 'financialSupport.form.errors.name'
  | 'financialSupport.form.errors.flatRateAmount'
  | 'financialSupport.form.errors.vaTypes';

/** Prüft das Formular und gibt einen i18n-Schlüssel zurück, oder null. */
export function validateFinancialSupportForm(
  values: FinancialSupportFormValues
): FinancialSupportFormError | null {
  if (values.name.trim().length === 0) return 'financialSupport.form.errors.name';

  const amount = Number(values.flatRateAmount.replace(',', '.'));
  if (values.flatRateAmount.trim().length === 0 || !Number.isFinite(amount) || amount < 0) {
    return 'financialSupport.form.errors.flatRateAmount';
  }

  if (values.vaTypes.length === 0) return 'financialSupport.form.errors.vaTypes';

  return null;
}

export function FinancialSupportFormDialog({
  open,
  entry,
  saveError,
  onOpenChange,
  onSave,
  isSaving,
}: Props) {
  const { t } = useTranslation(['settings', 'events']);
  const { data: eventTypes = [] } = useEventTypes();
  const sortedEventTypes = useMemo(
    () => [...eventTypes].sort((a, b) => a.name.localeCompare(b.name, 'de')),
    [eventTypes]
  );
  const [form, setForm] = useState<FinancialSupportFormValues>(emptyForm);
  const [error, setError] = useState<FinancialSupportFormError | null>(null);

  const isEdit = entry !== null;

  useEffect(() => {
    if (!open) return;
    setForm(
      entry
        ? {
            name: entry.name,
            audience: entry.audience,
            flatRateAmount: String(entry.flatRateAmount),
            vaTypes: entry.vaTypes,
            proofRequired: entry.proofRequired,
          }
        : emptyForm()
    );
    setError(null);
  }, [open, entry]);

  const handleSubmit = () => {
    const problem = validateFinancialSupportForm(form);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    onSave({
      id: entry?.id,
      name: form.name.trim(),
      audience: form.audience,
      flatRateAmount: Number(form.flatRateAmount.replace(',', '.')),
      vaTypes: form.vaTypes,
      proofRequired: form.proofRequired,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t(isEdit ? 'financialSupport.form.titleEdit' : 'financialSupport.form.titleCreate')}
          </DialogTitle>
          <DialogDescription>{t('financialSupport.form.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="financial-support-name">
              {t('financialSupport.form.fields.name')}
            </label>
            <Input
              id="financial-support-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Weinseminar-Pauschale"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">{t('events:form.fields.audience')}</label>
            <Select
              value={form.audience}
              onValueChange={(v) => setForm((prev) => ({ ...prev, audience: v as EventAudience }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(EventAudience).map((v) => (
                  <SelectItem key={v} value={v}>
                    {t(`events:audience.${v}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="financial-support-amount">
              {t('financialSupport.form.fields.flatRateAmount')}
            </label>
            <Input
              id="financial-support-amount"
              type="number"
              min={0}
              step="0.01"
              value={form.flatRateAmount}
              onChange={(e) => setForm((prev) => ({ ...prev, flatRateAmount: e.target.value }))}
              placeholder="100"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {t('financialSupport.form.fields.vaTypes')}
            </label>
            <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border p-3">
              {sortedEventTypes.map((eventType) => {
                const checked = form.vaTypes.includes(eventType.id);
                return (
                  <label key={eventType.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) =>
                        setForm((prev) => ({
                          ...prev,
                          vaTypes:
                            v === true
                              ? [...prev.vaTypes, eventType.id]
                              : prev.vaTypes.filter((id) => id !== eventType.id),
                        }))
                      }
                    />
                    {eventType.name}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="financial-support-proof-required"
              checked={form.proofRequired}
              onCheckedChange={(v) => setForm((prev) => ({ ...prev, proofRequired: v === true }))}
            />
            <label htmlFor="financial-support-proof-required" className="space-y-0.5 text-sm">
              <span className="block font-medium">{t('financialSupport.form.fields.proofRequired')}</span>
              <span className="block text-xs text-muted-foreground">
                {t('financialSupport.form.fields.proofRequiredHint')}
              </span>
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{t(error)}</p>}
          {!error && saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('financialSupport.form.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? t('financialSupport.form.saving') : t('financialSupport.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
